import { Html } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import { useEffect, useMemo, useState } from 'react';
import type { Graph3DNodeRendererProps } from '../../core/renderer.types';
import { createRoundedRectShape, getNodeDataString } from './nodeShapeUtils';
import { NodeTextHitPlane } from './NodeTextHitPlane';

const HTML_CONTENT_Z_INDEX_RANGE: [number, number] = [160, 0];

export interface Graph3DAvatarNodeData {
  avatarUrl?: string;
  avatarFallbackUrls?: string[];
  subtitle?: string;
  role?: string;
}

type AvatarData = Graph3DAvatarNodeData;

export function AvatarNode3D({
  node,
  runtime,
  theme,
  hovered,
  selected,
  focused,
  onHover,
  onSelect,
  onFocus,
}: Graph3DNodeRendererProps) {
  const status = runtime?.status ?? 'idle';
  const active =
    runtime?.active || runtime?.highlighted || hovered || selected || focused;
  const color =
    runtime?.color ??
    getNodeDataString(node.data, 'themeColor', theme.runtime[status]);
  const data = (node.data ?? {}) as AvatarData;
  const displayName = runtime?.message || node.label;
  const staticSubtitle = data.subtitle || data.role || '';
  const dedupedStaticSubtitle =
    staticSubtitle && staticSubtitle !== displayName ? staticSubtitle : '';
  const displaySubtitle =
    runtime?.detail ||
    (runtime?.message ? staticSubtitle : dedupedStaticSubtitle);
  const fill = active ? '#ffffff' : '#f8fafc';
  const border = active ? color : '#cbd5e1';
  const titleColor = active ? '#344054' : '#98a2b3';
  const subtitleColor = active ? '#475467' : '#98a2b3';
  const shape = useMemo(
    () =>
      createRoundedRectShape(
        node.resolvedSize.width,
        node.resolvedSize.height,
        14
      ),
    [node.resolvedSize.height, node.resolvedSize.width]
  );
  const extrudeSettings = useMemo(
    () => ({ depth: node.resolvedSize.depth, bevelEnabled: false }),
    [node.resolvedSize.depth]
  );
  const avatarSources = useMemo(
    () => resolveAvatarSources(data),
    [data.avatarFallbackUrls, data.avatarUrl]
  );
  const [avatarSourceIndex, setAvatarSourceIndex] = useState(0);
  const avatarMetrics = useMemo(() => {
    const scale = Math.min(
      node.resolvedSize.width / 220,
      node.resolvedSize.height / 84
    );
    return {
      scale,
      avatarSize: Math.round(42 * scale),
      paddingLeft: Math.round(20 * scale),
      paddingRight: Math.round(18 * scale),
      gap: Math.round(18 * scale),
      borderWidth: Math.max(1, Math.round(2 * scale)),
      titleFontSize: Math.max(12, Math.round(17 * scale)),
      titleLineHeight: Math.max(14, Math.round(20 * scale)),
      subtitleFontSize: Math.max(10, Math.round(13 * scale)),
      subtitleLineHeight: Math.max(12, Math.round(16 * scale)),
      subtitleGap: Math.max(2, Math.round(4 * scale)),
    };
  }, [node.resolvedSize.height, node.resolvedSize.width]);
  const defaultAvatarIcon = useMemo(
    () => createAvatarIconDataUri(active ? color : '#98a2b3', '#ffffff'),
    [active, color]
  );
  const currentAvatarUrl = avatarSources[avatarSourceIndex] || '';

  useEffect(() => {
    setAvatarSourceIndex(0);
  }, [avatarSources]);

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    onSelect(node.id);
    onFocus(node.id);
  };

  const handleAvatarError = () => {
    setAvatarSourceIndex(index => {
      if (index + 1 < avatarSources.length) {
        return index + 1;
      }
      return avatarSources.length;
    });
  };

  return (
    <group
      position={[
        node.resolvedPosition.x,
        node.resolvedPosition.y,
        node.resolvedPosition.z,
      ]}
      onClick={handleClick}
      onPointerEnter={event => {
        event.stopPropagation();
        onHover(node.id);
      }}
      onPointerLeave={event => {
        event.stopPropagation();
        onHover(null);
      }}
    >
      <mesh position={[0, 0, -node.resolvedSize.depth / 2]}>
        <extrudeGeometry args={[shape, extrudeSettings]} />
        <meshStandardMaterial
          color={fill}
          emissive={border}
          emissiveIntensity={active ? 0.24 : 0.08}
          roughness={0.38}
          metalness={0.02}
          transparent
          opacity={runtime?.hidden ? 0.18 : 0.98}
        />
      </mesh>
      <NodeTextHitPlane
        width={node.resolvedSize.width}
        height={node.resolvedSize.height}
        z={node.resolvedSize.depth / 2 + 7}
      />
      <Html
        center
        transform
        distanceFactor={400}
        zIndexRange={HTML_CONTENT_Z_INDEX_RANGE}
        position={[0, 0, node.resolvedSize.depth / 2 + 7]}
      >
        <div
          style={{
            width: node.resolvedSize.width,
            height: node.resolvedSize.height,
            pointerEvents: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: avatarMetrics.paddingLeft,
            paddingRight: avatarMetrics.paddingRight,
            boxSizing: 'border-box',
            gap: avatarMetrics.gap,
          }}
        >
          <div
            style={{
              width: avatarMetrics.avatarSize,
              minWidth: avatarMetrics.avatarSize,
              height: avatarMetrics.avatarSize,
              borderRadius: '50%',
              overflow: 'hidden',
              // border: `${avatarMetrics.borderWidth}px solid ${active ? color : '#98a2b3'}`,
              backgroundColor: '#ffffff',
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          >
            <img
              src={currentAvatarUrl || defaultAvatarIcon}
              alt=""
              onError={currentAvatarUrl ? handleAvatarError : undefined}
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                color: titleColor,
                fontFamily:
                  'system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif',
                fontSize: avatarMetrics.titleFontSize,
                fontWeight: 700,
                lineHeight: `${avatarMetrics.titleLineHeight}px`,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {displayName}
            </div>
            {displaySubtitle ? (
              <div
                style={{
                  marginTop: avatarMetrics.subtitleGap,
                  color: subtitleColor,
                  fontFamily:
                    'system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif',
                  fontSize: avatarMetrics.subtitleFontSize,
                  fontWeight: 500,
                  lineHeight: `${avatarMetrics.subtitleLineHeight}px`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displaySubtitle}
              </div>
            ) : null}
          </div>
        </div>
      </Html>
    </group>
  );
}

function resolveAvatarSources(data: AvatarData): string[] {
  const seen = new Set<string>();
  return [data.avatarUrl || '', ...(data.avatarFallbackUrls || [])]
    .map(url => url.trim())
    .filter(url => {
      if (!url || seen.has(url)) {
        return false;
      }
      seen.add(url);
      return true;
    });
}

function createAvatarIconDataUri(color: string, bg: string): string {
  const svg = `<?xml version="1.0" encoding="UTF-8"?><!DOCTYPE svg>
  <svg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 64 64'>
    ${makeAvatarIconSvg(color, bg, 32, 32, 30, 2)}
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function makeAvatarIconSvg(
  color: string,
  bg: string,
  centerX: number,
  centerY: number,
  radius: number,
  strokeWidth: number
): string {
  const headY = centerY - radius * 0.18;
  const bodyTopY = centerY + radius * 0.2;
  return `
    <circle cx='${centerX}' cy='${centerY}' r='${radius}' fill='${bg}' stroke='${color}' stroke-width='${strokeWidth}'/>
    <circle cx='${centerX}' cy='${headY}' r='${radius * 0.28}' fill='none' stroke='${color}' stroke-width='${strokeWidth}'/>
    <path d='M${centerX - radius * 0.56} ${bodyTopY + radius * 0.48}c${radius * 0.1}-${radius * 0.32} ${radius * 0.46}-${radius * 0.56} ${radius * 0.56}-${radius * 0.56}s${radius * 0.46} ${radius * 0.24} ${radius * 0.56} ${radius * 0.56}' fill='none' stroke='${color}' stroke-width='${strokeWidth}' stroke-linecap='round'/>
  `;
}
